import type { AxiosInstance, AxiosStatic } from 'axios';
import { KNOWN_DISALLOWED_CATALOG_IDS, KNOWN_ODP_CATALOG_IDS } from './constants';

export function getSubdomainCatalogIdFromUrl(url: string, disallowedCatalogIds : string[] = KNOWN_DISALLOWED_CATALOG_IDS): string {
  const host = new URL(url).host;
  const maybeSubdomain = getLeftmostSubdomain(host);
  return disallowedCatalogIds.includes(maybeSubdomain) ? '' : maybeSubdomain;
}

export function formatDatetime(date: string) {
  // return new Date(this.value).toLocaleDateString('de', { year: 'numeric', month: '2-digit', day: '2-digit' });
  // validate and format date to dd.mm.yyyy
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleDateString('de', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/**
 * Maps some locale codes to German region
 */
export const localeCodeToGermanMini = {
  // Just add a bunch of European countries.
  // We'll just need 'de' anyways, right?
  'de': 'Deutsch',
  'en': 'Englisch',
  'fr': 'Französisch',
  'es': 'Spanisch',
  'it': 'Italienisch',
  'pt': 'Portugiesisch',
  'nl': 'Niederländisch',
  'pl': 'Polnisch',
  'cs': 'Tschechisch',
  'da': 'Dänisch',
  'fi': 'Finnisch',
  'el': 'Griechisch',
  'hu': 'Ungarisch',
  'ga': 'Irisch',
  'lv': 'Lettisch',
  'lt': 'Litauisch',
  'mt': 'Maltesisch',
  'ro': 'Rumänisch',
  'sk': 'Slowakisch',
  'sl': 'Slowenisch',
  'sv': 'Schwedisch',
  'hr': 'Kroatisch',
  'bg': 'Bulgarisch',
  'et': 'Estnisch',
}

export function getLeftmostSubdomain(hostname: string) {
  // Strip port number if present
  const domainWithoutPort = hostname.split(':')[0];

  // Construct regex patterns
  const secondLevelPattern = '(co\\.uk|com\\.au|co\\.nz|co\\.za|com\\.br)';
  // Update the generalPattern to match any subdomain before the main domain and TLD
  const generalPattern = new RegExp(`^(www\\.)?(.*?)\\.(?:[^\\.]+\\.${secondLevelPattern}|[^\\.]+\\.[^\\.]+)$`);
  const localPattern = /^([^.]+)\.local$/;

  // Match against .local pattern
  const localMatch = domainWithoutPort.match(localPattern);
  if (localMatch) {
      return localMatch[1];
  }

  // Match the subdomain for general domains
  const match = domainWithoutPort.match(generalPattern);

  if (match && match[2] && match[2] !== 'www') {
      // Split the subdomains and return the leftmost one
      return match[2].split('.')[0];
  }

  return '';
}

export async function isOdp(catalogId: string, hubSearchApiBaseUrl: string, disallowedCatalogIds: string[]) : Promise<{ isOdp: boolean; reason?: 'INVALID_CATALOG_ID' | 'DISALLOWED_CATALOG' | 'FETCH_ERROR' | 'UNKNOWN_ERROR' }> {
  if (disallowedCatalogIds.includes(catalogId)) {
    return { isOdp: false, reason: 'DISALLOWED_CATALOG' };
  }

  if (KNOWN_ODP_CATALOG_IDS.includes(catalogId)) {
    return { isOdp: true };
  }

  try {
    const url = `${hubSearchApiBaseUrl}catalogues/${catalogId}`;
    const response = await fetch(url, { method: 'GET' });

    if (!response.ok) {
      return { isOdp: false, reason: 'FETCH_ERROR' };
    }

    const data = await response.json();
    const ok = data?.result?.is_open_data_presence === true;
    return { isOdp: ok, reason: ok ? undefined : 'INVALID_CATALOG_ID' };
  } catch (error) {
    return { isOdp: false, reason: 'UNKNOWN_ERROR' };
  }
}

/**
 * Patches the Axios instance to include the openDataPresence facet in dataset search requests made to the hub search API.
 * @param axiosInstance - The Axios instance or static object.
 * @param hubSearchApiBaseUrl - The base URL of the hub search API.
 * @param openDataPresence - The value of the openDataPresence facet.
 */
export function patchAxiosDatasetSearchWithOpenDataPresenceFacet({ axiosInstance, hubSearchApiBaseUrl, openDataPresence }: { axiosInstance: AxiosInstance | AxiosStatic; hubSearchApiBaseUrl: string; openDataPresence: string; }) {
  const interceptor = axiosInstance.interceptors.request.use(async (config) => {
    const isHubSearchApi = config?.url?.includes(hubSearchApiBaseUrl);
    const searchParams = config.params
    const isDatasetSearch = searchParams?.filter === 'dataset';

    if (!searchParams || !hubSearchApiBaseUrl || !isHubSearchApi || !isDatasetSearch) return config;

    const facetsParam = searchParams?.facets;

    // The hub search API expects `facets` as a single JSON string query param (legacy behavior).
    // If we leave it as an object, Axios (or a params serializer like `qs`) may expand it into
    // `facets[format][0]=CSV`, which changes backend parsing.
    let facetsObj: Record<string, unknown> = {};
    if (typeof facetsParam === 'string') {
      try {
        const parsed = JSON.parse(facetsParam);
        if (parsed && typeof parsed === 'object') {
          facetsObj = parsed as Record<string, unknown>;
        }
      } catch {
        // If it's not JSON, keep it as empty object so we can still inject open_data_presence.
        facetsObj = {};
      }
    } else if (facetsParam && typeof facetsParam === 'object') {
      facetsObj = facetsParam as Record<string, unknown>;
    }

    let superCatalogFacet = '';
    const superCatalogValue = facetsObj['superCatalog'];
    if (Array.isArray(superCatalogValue) && superCatalogValue.length > 0 && typeof superCatalogValue[0] === 'string') {
      superCatalogFacet = superCatalogValue[0];
    }

    const facetsWithOpenDataPresence = {
      ...facetsObj,
      ...!!superCatalogFacet && { open_data_presence: [openDataPresence || superCatalogFacet] },
      // and also exclude superCatalog from the facets
      superCatalog: [],
    };

    config.params.facets = JSON.stringify(facetsWithOpenDataPresence);
    config.params.facetOperator = 'OR';

    return config;
  });

  return interceptor;
}

/**
 * Patches the Axios instance to append HVD fields to the includes parameter in dataset search requests made to the hub search API.
 * @param axiosInstance - The Axios instance or static object.
 * @param hubSearchApiBaseUrl - The base URL of the hub search API.
 */
export function patchAxiosDatasetSearchWithHvdIncludes({ axiosInstance, hubSearchApiBaseUrl }: { axiosInstance: AxiosInstance | AxiosStatic; hubSearchApiBaseUrl: string; }) {
  const interceptor = axiosInstance.interceptors.request.use(async (config) => {
    const isHubSearchApi = config?.url?.includes(hubSearchApiBaseUrl);
    const searchParams = config.params;
    const isDatasetSearch = searchParams?.filter === 'dataset';

    if (!searchParams || !hubSearchApiBaseUrl || !isHubSearchApi || !isDatasetSearch) return config;

    const currentIncludes = searchParams.includes || '';
    const includesArray = currentIncludes ? currentIncludes.split(',').map((item: string) => item.trim()) : [];

    const hvdFields = ['is_hvd', 'isHvd'];
    const fieldsToAdd = hvdFields.filter(field => !includesArray.includes(field));

    if (fieldsToAdd.length > 0) {
      const updatedIncludes = [...includesArray, ...fieldsToAdd].join(',');
      config.params.includes = updatedIncludes;
    }

    return config;
  });

  return interceptor;
}
